import { faDownload, faFilePdf } from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  ArrowRightIcon,
  DownloadIcon,
  FlexBox,
  JoonColorExpanded,
  JoonUIColor,
  SPACING,
  Typography,
  UserRole,
} from "@joonapp/web-shared"
import type {
  BaseMessage,
  FileMessage,
  MultipleFilesMessage,
} from "@sendbird/chat/message"
import dayjs from "dayjs"
import { useMemo, useState } from "react"

import MessageMenu from "./MessageMenu"
import { useImageModalStore } from "../../pages/clients/routines/ImageModal"
import { useGetProfileById } from "../../pages/home/hooks/useGetProfileById"
import { renderMessageWithLinks } from "../../util/strings"
import ProfileImage from "../profileImage/ProfileImage"

interface MessageItemProps {
  message: BaseMessage
  isMyMessage: boolean
  showTimestamp?: boolean
  showAvatar?: boolean
}

const MessageItem = ({
  message,
  isMyMessage,
  showTimestamp = true,
  showAvatar = true,
}: MessageItemProps) => {
  const [isHovered, setIsHovered] = useState(false)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  // @ts-ignore
  const userProfile = useGetProfileById(Number(message.sender?.userId))
  const isClient = userProfile?.role === UserRole.CLIENT
  const timestamp = (
    <Typography
      variant="bodyXSmall"
      color={JoonUIColor.text.secondary}
      style={{
        alignSelf: "flex-end",
        fontSize: "0.75rem",
        whiteSpace: "nowrap",
      }}
    >
      {dayjs(message.createdAt).format("h:mm A")}
    </Typography>
  )

  const isEdited = message.updatedAt > 0
  const showMenu = isHovered || isMenuOpen

  const ogMetaData = message.ogMetaData
  const isFileMessage = message.messageType === "file"

  const messageText = useMemo(
    () =>
      ogMetaData
        ? renderMessageWithLinks(message.message, isMyMessage)
        : message.message,
    [message.message, isMyMessage, ogMetaData]
  )

  return (
    <FlexBox
      direction="row"
      justify={isMyMessage ? "flex-end" : "flex-start"}
      wrap={false}
      align="flex-end"
      gap={SPACING.space2}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        if (!isMenuOpen) {
          setIsHovered(false)
        }
      }}
    >
      {!isMyMessage && (
        <div style={{ width: 32 }}>
          {showAvatar && (
            <ProfileImage
              // @ts-ignore
              profileUrl={userProfile?.profile_image_url}
              userName={userProfile?.user.name || ""}
              size="small"
              color={isClient ? "purple" : "orange"}
            />
          )}
        </div>
      )}

      {isMyMessage &&
        (showMenu ? (
          <MessageMenu
            message={message}
            isMyMessage={isMyMessage}
            setIsMenuOpen={setIsMenuOpen}
          />
        ) : (
          showTimestamp && timestamp
        ))}

      <FlexBox
        direction="column"
        gap={SPACING.space1}
        style={{ maxWidth: "min(70vw, 400px)", width: "fit-content" }}
      >
        {message.parentMessage && (
          <>
            <FlexBox
              direction="row"
              align="center"
              gap={SPACING.space1}
              style={{
                marginBottom: SPACING.space1,
                opacity: 0.7,
              }}
            >
              <ArrowRightIcon size={16} color={JoonUIColor.text.secondary} />
              <Typography
                variant="bodyXSmall"
                color={JoonUIColor.text.secondary}
              >
                Replied to a message
              </Typography>
            </FlexBox>
            <div
              style={{
                width: "fit-content",
                padding: SPACING.space2,
                borderRadius: SPACING.space1,
                background: JoonUIColor.background.xlightGray,
                marginBottom: `-${SPACING.space2}`,
              }}
            >
              <Typography
                variant="bodyXSmall"
                color={JoonUIColor.text.secondary}
                style={{
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {message.parentMessage.message}
              </Typography>
            </div>
          </>
        )}

        <div
          style={{
            backgroundColor: isMyMessage
              ? JoonColorExpanded.purple600
              : isClient
              ? JoonUIColor.background.lightAccent
              : JoonColorExpanded.orange100,
            borderRadius: SPACING.space2,
            opacity: isHovered ? 0.8 : 1,
            transition: "opacity 0.2s ease-in-out",
          }}
        >
          <div style={{ padding: `${SPACING.space2} ${SPACING.space3}` }}>
            {isFileMessage ? (
              renderFileMessage(message as FileMessage, isMyMessage)
            ) : (
              <>
                <Typography
                  variant="bodySmall"
                  color={
                    isMyMessage
                      ? JoonUIColor.text.inverted
                      : JoonUIColor.text.primary
                  }
                  style={{ whiteSpace: "pre-wrap" }}
                >
                  {messageText}
                  {isEdited && (
                    <Typography
                      variant="bodyXSmall"
                      color={JoonUIColor.background.lightGray}
                    >
                      {" "}
                      (edited)
                    </Typography>
                  )}
                </Typography>
              </>
            )}
          </div>
          {ogMetaData && (
            <a
              href={ogMetaData.url || ""}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                textDecoration: "none",
                color: "inherit",
                marginTop: SPACING.space2,
                display: "block",
                backgroundColor: JoonUIColor.background.lightGray,
                borderRadius: SPACING.space2,
              }}
            >
              <FlexBox direction="column" gap={SPACING.space2}>
                {ogMetaData.defaultImage && (
                  <img
                    src={ogMetaData.defaultImage.url}
                    alt={ogMetaData.title || "Link preview"}
                    style={{
                      width: "100%",
                      maxHeight: "200px",
                      borderRadius: SPACING.space1,
                      objectFit: "cover",
                    }}
                  />
                )}
                <FlexBox
                  direction="column"
                  gap={SPACING.space1}
                  style={{
                    padding: SPACING.space3,
                    paddingTop: SPACING.space2,
                  }}
                >
                  <Typography variant="bodySmallBold">
                    {ogMetaData.title}
                  </Typography>
                  {ogMetaData.description && (
                    <Typography
                      variant="bodyXSmall"
                      color={JoonUIColor.text.secondary}
                      style={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {ogMetaData.description}
                    </Typography>
                  )}
                </FlexBox>
              </FlexBox>
            </a>
          )}
        </div>
      </FlexBox>

      {!isMyMessage &&
        (showMenu ? (
          <MessageMenu
            message={message}
            isMyMessage={isMyMessage}
            setIsMenuOpen={setIsMenuOpen}
          />
        ) : (
          showTimestamp && timestamp
        ))}
    </FlexBox>
  )
}

export default MessageItem

const renderFileMessage = (fileMessage: FileMessage, isMyMessage: boolean) => {
  const { url, type, name } = fileMessage

  if (fileMessage.isMultipleFilesMessage())
    return <MultiImageMessage fileMessage={fileMessage} />

  if (url.includes(".heic")) {
    return (
      <FlexBox align="center" gap={SPACING.space2} wrap={false}>
        <Typography variant="bodySmall">Image format not supported</Typography>
        <DownloadButton url={url} name={name} />
      </FlexBox>
    )
  }

  if (type.startsWith("image/")) {
    return <ImageMessage url={url} name={name} />
  } else if (type.startsWith("video/")) {
    return (
      <video
        controls
        src={url}
        style={{
          maxWidth: "100%",
          maxHeight: "300px",
          borderRadius: SPACING.space1,
        }}
      >
        <track kind="captions" />
      </video>
    )
  } else if (type.startsWith("audio/")) {
    return (
      <FlexBox direction="row" align="center" gap={SPACING.space2} wrap={false}>
        <Typography
          variant="bodySmall"
          color={
            isMyMessage ? JoonUIColor.text.inverted : JoonUIColor.text.primary
          }
        >
          {name}
        </Typography>
        <DownloadButton url={url} name={name} />
      </FlexBox>
    )
  } else if (type.startsWith("application/pdf")) {
    return (
      <a
        href={url}
        download={name}
        className="flex flex-row items-center gap-2 bg-white rounded-sm px-2 py-1"
      >
        <FontAwesomeIcon
          icon={faFilePdf}
          fontSize={16}
          color={JoonUIColor.text.primaryAccent}
        />
        <Typography
          variant="bodySmall"
          color={JoonUIColor.text.primary}
          className="mr-2"
        >
          {name}
        </Typography>
        <FontAwesomeIcon
          icon={faDownload}
          fontSize={12}
          color={JoonUIColor.text.primaryAccent}
        />
      </a>
    )
  } else {
    return (
      <Typography
        variant="bodySmall"
        color={
          isMyMessage ? JoonUIColor.text.inverted : JoonUIColor.text.primary
        }
      >
        File type not supported.
      </Typography>
    )
  }
}

const DownloadButton = ({ url, name }: { url: string; name: string }) => {
  return (
    <a
      href={url}
      download={name}
      style={{
        textDecoration: "none",
        padding: `${SPACING.space1} ${SPACING.space2}`,
        borderRadius: SPACING.space1,
        cursor: "pointer",
        background: JoonUIColor.background.primaryNeutral,
        gap: SPACING.space1,
        border: `1px solid ${JoonUIColor.border.accent}`,
        display: "flex",
        alignItems: "center",
      }}
    >
      <Typography variant="bodySmall" color={JoonUIColor.text.primaryAccent}>
        Download
      </Typography>
      <DownloadIcon color={JoonUIColor.text.primaryAccent} size={12} />
    </a>
  )
}

const ImageMessage = ({ url, name }: { url: string; name: string }) => {
  const { onOpen: openImageModal } = useImageModalStore()

  return (
    <button
      onClick={() => openImageModal(url, "Image attachment")}
      className="rounded-sm overflow-hidden"
    >
      <img
        src={url}
        alt={name}
        style={{
          maxWidth: "100%",
          maxHeight: "300px",
          borderRadius: SPACING.space1,
        }}
      />
    </button>
  )
}

const MultiImageMessage = ({
  fileMessage,
}: {
  fileMessage: FileMessage & MultipleFilesMessage
}) => {
  const { onOpen: openImageModal } = useImageModalStore()

  return (
    <div className="flex flex-row gap-1 items-start max-w-[400px] flex-wrap">
      {fileMessage.fileInfoList.map((file) => (
        <button
          onClick={() => openImageModal(file.url, "Image attachment")}
          className="rounded-sm overflow-hidden"
        >
          <img
            src={file.url}
            alt={file.url}
            className="max-w-[200px] h-[200px] w-auto object-contain"
          />
        </button>
      ))}
    </div>
  )
}
